/**
 * Sentry SDK Initialization
 *
 * Initializes Sentry error tracking and performance monitoring.
 * Must be called before React renders to capture all errors.
 *
 * Implements:
 * - FR-001: Initialize before React renders
 * - FR-006: Differentiate dev/prod environments
 * - FR-007: Sensitive data filtering
 * - FR-008: 100% error sample rate
 * - FR-011: Release tracking
 */

import * as Sentry from '@sentry/react';
import {runtimeConfig} from './config/runtime-config';
import {beforeSendHandler} from './lib/sentry-filters';
import {isDebug, isTrace} from './lib/debug';
import {isTruthy} from "@/lib/utils.ts";

// [SEL-6] Sentry SDK initialization, environment detection, and test event dispatch
export const instrumentationProvider: string = "posthog";

/**
 * Send a test event to the configured instrumentation provider
 *
 * @param subject - Event subject/name (e.g., "admin_test_event")
 * @param message - Custom message to include in the test event
 * @param metadata - Additional metadata to attach to the event
 * @returns Object with success status and provider name
 */
export interface TestEventResult {
  success: boolean;
  provider: string;
  timestamp: string;
  eventId?: string;
}

export function sendTestEvent(subject: string, message: string, metadata?: Record<string, unknown>): TestEventResult {
  const timestamp = new Date().toISOString();
  const eventData = {
    subject,
    message,
    timestamp,
    triggeredBy: 'admin-test-button',
    environment: runtimeConfig.config?.environment || 'development',
    ...metadata,
  };

  if (instrumentationProvider === 'sentry') {
    Sentry.logger.info('User triggered test event', eventData);
    const error = new Error(message);
    const eventId = Sentry.captureException(error, {
      level: 'info',
      tags: {
        type: 'test-event',
        subject: subject,
        triggered_at: timestamp,
      },
      extra: eventData,
    });
    return {
      success: true,
      provider: 'Sentry',
      timestamp,
      eventId,
    };
  } else if (instrumentationProvider === 'posthog') {
    // PostHog needs to be called from React context with usePostHog
    // This will be handled in the component level
    return {
      success: false,
      provider: 'PostHog',
      timestamp,
    };
  }

  return {
    success: false,
    provider: 'unknown',
    timestamp,
  };
}

/**
 * Initialize Sentry SDK with environment-specific configuration
 *
 * Environment detection:
 * - Test mode: Skip initialization (import.meta.env.MODE === 'test')
 * - Development: Skip sending errors (import.meta.env.PROD === false)
 * - Production: Full error tracking and performance monitoring
 *
 * @returns Promise that resolves when initialization is complete
 */
export async function initializeSentry(): Promise<void> {
  try {
    // Skip initialization in test mode
    if (import.meta.env.MODE === 'test') {
      if (isDebug()) {
        console.log('[Sentry] Skipping initialization (test mode)');
      }
      return;
    }

    // Check if DSN is available
    const dsn = runtimeConfig.config?.sentry?.dsn || import.meta.env.VITE_SENTRY_DSN;
    if (!dsn) {
      if (isDebug()) {
        console.log('[Sentry] Skipping initialization (no DSN configured)');
      }
      return;
    }

    // Determine environment
    const environment = runtimeConfig.config?.sentry?.environment || import.meta.env.VITE_SENTRY_ENVIRONMENT || runtimeConfig.config?.environment || 'development';
    const release = runtimeConfig.config?.version || 'development';

    // Determine if we should send errors (only in production)
    const isProduction = environment == 'production' || environment == 'PROD';
    const shouldSendErrors = isTruthy(runtimeConfig.config?.sentry?.enabled ?? true) && isTruthy(runtimeConfig.config?.sentry?.send_errors ?? true);

    if (isDebug()) {
      console.log('[Sentry] Initializing with config:', {
        dsn: dsn.substring(0, 20) + '...',
        environment,
        release,
        isProd: import.meta.env.PROD,
        shouldSendErrors,
      });
    }

    // Import Supabase client and get actual instance (not Proxy) for Sentry integration
    // The Proxy pattern breaks Sentry's instrumentation, so we need the real client
    const { getSupabaseClient } = await import('./integrations/supabase/client');
    const supabaseClient = getSupabaseClient();

    // Build integrations array with conditional performance monitoring
    const integrations = [
      Sentry.extraErrorDataIntegration(),
      Sentry.featureFlagsIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.moduleMetadataIntegration(),
      Sentry.supabaseIntegration({ supabaseClient }),
      Sentry.replayCanvasIntegration(),
      Sentry.replayIntegration({
        // Privacy settings (FR-012: Exclude PII)
        maskAllText: isProduction, // Mask all text by default to prevent PII capture
        maskAllInputs: isProduction, // Mask all form inputs
        blockAllMedia: isProduction, // Block images/video that might contain sensitive content

        // Unmask non-sensitive UI elements for better debugging
        unmask: [
          '.unmask-replay', // Explicitly unmaskable elements
        ],

        // Block sensitive elements entirely
        block: [
          '[data-sentry-block]', // Manual blocking
          'input[type="password"]', // Password fields
          'input[type="email"]', // Email addresses
          '.user-email', // User email displays
          '.user-name', // User name displays
        ],
      }),
      Sentry.feedbackIntegration({
        colorScheme: 'system',
        autoInject: false, // Manual injection via error boundary
      }),
    ];
    const tracePropagationTargets: (RegExp|string)[] = [
      /bookwizard\.app/,
      /bookwizard\.net/,
      /self-pub\.net/,
      /supabase\.co/, // Include Supabase
    ];
    if (isDebug()) {
      tracePropagationTargets.push(
        "localhost",
        "https://74760fdc-30e9-40d3-8d01-0f900675772f.lovableproject.com",
        "https://lovable.dev",
      );
    }

    // Enable performance monitoring only in debug mode
    if (isDebug()) {
      integrations.push(
        Sentry.browserProfilingIntegration()
      );
    }

    // Enable performance monitoring only in debug mode
    if (isTrace()) {
      integrations.push(
        Sentry.browserTracingIntegration(),
      );
    }

    // Initialize Sentry
    // noinspection TypeScriptUnresolvedReference,TypeScriptValidateTypes
    Sentry.init({
      dsn,
      environment,
      release,

      // Integrations
      integrations,

      // Performance Monitoring (only in debug mode)
      tracesSampleRate: 1.0, // 100% of transactions
      tracePropagationTargets: tracePropagationTargets,

      // Session Replay
      replaysSessionSampleRate: 0.25, // 25% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

      // Data filtering
      beforeSend: (event: Sentry.ErrorEvent, hint: Sentry.EventHint) => {
        // Filter sensitive data
        const filtered = beforeSendHandler(event, hint);

        // In development, log but don't send
        if (isDebug()) {
          console.log('[Sentry] Captured error (not sent in dev):', {
            message: event.message,
            level: event.level,
            user: event.user,
          });
        }
        return shouldSendErrors ? filtered : null; // Don't send in development
      },

      // Enable debug logging in development
      debug: isDebug() && !isProduction,

      // Enable sending default PII (we filter it in beforeSend)
      sendDefaultPii: false, // We manually set user context instead
      enableLogs: true
    });

    if (isDebug()) {
      console.log('[Sentry] Initialization complete', {
        environment,
        release,
        sendingErrors: shouldSendErrors,
      });
    }
  } catch (error) {
    // Never let Sentry initialization break the app
    console.error('[Sentry] Initialization failed:', error);
  }
}
